import React, { useCallback, useEffect, useState } from "react";
import { MotionConfig } from "framer-motion";
import Chronologicle from "./Chronologicle";
import TitleScreen from "./TitleScreen";
import ProfileMenu from "./ProfileMenu";
import MenuOverlay from "./MenuOverlay";
import HowToPlayModal from "./HowToPlayModal";
import Leaderboard from "./Components/Leaderboard";
import { getChronologicleDay, getOptimisticDay } from "./getChronologicleDay"; // 👈 Import new helper
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebase/firebase";
import { onAuthStateChanged } from "firebase/auth";
import BackgroundMusic from "./Components/BackgroundMusic";
import MyStatsPage from "./MyStatsPage";
import {
  clearDailySessionStarted,
  hasCurrentDailySession,
  markDailySessionStarted,
} from "./gameSession";
import DailyStatusScreen from "./DailyStatusScreen";
import {
  getDailyAvailability,
  getDailyContentDay,
  getDailyEventsForDay,
} from "./dailyContent";
import { calculateDayFromStart, CHRONOLOGICLE_START_DATE } from "./dayCalculation";

const DAILY_EVENTS_URL = "https://docs.google.com/spreadsheets/d/1ajES33vP06TDV4WwW1oCQ9BxHdD6S7-A_W3LDuHj1zc/export?format=csv";
const PREMIUM_EVENTS_URL = "https://docs.google.com/spreadsheets/d/12oNAV9iwRxuQBwLKWW25c4LgyTpR_oPXwyD3N16LQcs/export?format=csv";
const DAILY_EVENTS_SNAPSHOT_URL = "/data/daily-events.json";

const parseRemoteCsv = (Papa, url) => new Promise((resolve, reject) => {
  Papa.parse(url, {
    download: true,
    header: true,
    complete: (result) => resolve(result.data || []),
    error: reject,
  });
});

const cleanDailyRows = (rows) => rows
  .filter((event) => event.Event && event.Year && event.Day)
  .map((event) => ({
    event: event.Event.trim(),
    year: parseInt(event.Year),
    day: parseInt(event.Day),
    id: event.Event.trim(),
  }));

export const writeServerTime = async () => {
  try {
    await setDoc(doc(db, "config", "serverTime"), {
      now: serverTimestamp()
    });
    console.log("✅ Server time written");
  } catch (err) {
    console.error("🔥 Failed to write server time:", err);
  }
};

const getUniqueYearEvents = (pool, count) => {
  const stopWords = new Set(["the", "a", "as", "is", "of", "and", "in", "to", "for", "on", "by", "at", "an", "with", "from", "TV", "novel", "film", "show"]);
  const shuffled = [...pool].sort(() => 0.5 - Math.random());
  const result = [];
  const usedYears = new Set();
  const usedWords = new Set();

  for (const event of shuffled) {
    if (!event || !event.event || !event.year) continue;
    if (usedYears.has(event.year)) continue;

    const words = event.event.toLowerCase().split(/[^a-z]+/).filter((w) => w && !stopWords.has(w));
    if (words.some((w) => usedWords.has(w))) continue;

    usedYears.add(event.year);
    words.forEach((w) => usedWords.add(w));
    result.push(event);
    if (result.length === count) break;
  }

  return result;
};

function App() {
  const USE_RANDOM_POOL = false; // ← flip this between true and false
  const [authChecked, setAuthChecked] = useState(false);
  const [authUser, setAuthUser] = useState(null);

  // ⚡️ Optimized Start:
  // 1. Load cached "Server Day" instantly (using synchronized offset)
  // 2. Button shows immediately
  // 3. Background fetch validates/updates precise server time a moment later
  const [currentDay, setCurrentDay] = useState(getOptimisticDay());

  const [hasStarted, setHasStarted] = useState(() => localStorage.getItem("hasStarted") === "true");
  const [, setCurrentScore] = useState(0);
  const [displayName, setDisplayName] = useState(() => localStorage.getItem("displayName") || "");
  const [equippedEmblem, setEquippedEmblem] = useState("13guest.png");
  const [equippedTitle, setEquippedTitle] = useState("the Chronologicle");
  const [showLocker, setShowLocker] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [showHowTo, setShowHowTo] = useState(false);
  const [activePanel, setActivePanel] = useState(() => localStorage.getItem("activePanel") || "title"); // "title" | "game" | "leaderboard" | "stats"
  const [isPremiumMode, setIsPremiumMode] = useState(() => localStorage.getItem("isPremiumMode") === "true");
  const [isMuted] = useState(false);
  const [scoreFinalized, setScoreFinalized] = useState(false);
  const [menuClicked, setMenuClicked] = useState(false);
  const [previousPanel, setPreviousPanel] = useState(null); // Track where user came from for back navigation
  const [autoShowAlreadyPlayed, setAutoShowAlreadyPlayed] = useState(false); // Auto-show AlreadyPlayedScreen when returning to title

  const [loadedDailyEvents, setLoadedDailyEvents] = useState([]);
  const [loadedPremiumEvents, setLoadedPremiumEvents] = useState([]);
  const [initialEvents, setInitialEvents] = useState([]);
  const [dailyLoadState, setDailyLoadState] = useState("loading");

  const loadEvents = useCallback(async () => {
    setDailyLoadState("loading");

    try {
      const response = await fetch(DAILY_EVENTS_SNAPSHOT_URL, { cache: "force-cache" });
      if (!response.ok) throw new Error(`Daily snapshot request failed: ${response.status}`);
      const cleanedDailyEvents = await response.json();
      const serverDate = response.headers.get("date");
      if (serverDate) {
        setCurrentDay(calculateDayFromStart(CHRONOLOGICLE_START_DATE, new Date(serverDate)));
      }

      if (cleanedDailyEvents.length === 0) {
        throw new Error("The daily content response did not contain any valid events.");
      }

      setLoadedDailyEvents(cleanedDailyEvents);
      setDailyLoadState("ready");
    } catch (error) {
      console.warn("Hosted daily snapshot failed; trying the source feed", error);
      try {
        const Papa = await import("papaparse");
        const cleanedDailyEvents = cleanDailyRows(await parseRemoteCsv(Papa, DAILY_EVENTS_URL));
        if (cleanedDailyEvents.length === 0) throw new Error("The source feed did not contain valid events.");
        setLoadedDailyEvents(cleanedDailyEvents);
        setDailyLoadState("ready");
      } catch (fallbackError) {
        console.warn("Daily events failed to load:", fallbackError);
        setDailyLoadState("error");
      }
    }
  }, []);

  const loadPremiumEvents = useCallback(async () => {
    if (loadedPremiumEvents.length >= 5) return loadedPremiumEvents;
    const Papa = await import("papaparse");
    const premiumRows = await parseRemoteCsv(Papa, PREMIUM_EVENTS_URL);
    const cleanedPremiumEvents = premiumRows
      .filter((event) => event.Event && event.Year)
      .map((event) => ({
        event: event.Event.trim(),
        year: parseInt(event.Year),
        id: event.Event.trim(),
      }));
    setLoadedPremiumEvents(cleanedPremiumEvents);
    return cleanedPremiumEvents;
  }, [loadedPremiumEvents]);

  const contentDay = getDailyContentDay(currentDay);
  const dailyEventsForCurrentDay = getDailyEventsForDay(loadedDailyEvents, contentDay);
  const dailyAvailability = getDailyAvailability({
    events: loadedDailyEvents,
    day: currentDay,
    loadState: dailyLoadState,
  });
  const appReady = dailyAvailability !== "loading";

  const retryDailyResources = useCallback(async () => {
    setDailyLoadState("loading");
    const [day] = await Promise.all([getChronologicleDay(false), loadEvents()]);
    setCurrentDay(day);
  }, [loadEvents]);

  // 💾 Persist UI State
  useEffect(() => {
    localStorage.setItem("activePanel", activePanel);
    localStorage.setItem("hasStarted", hasStarted);
    localStorage.setItem("isPremiumMode", isPremiumMode);
  }, [activePanel, hasStarted, isPremiumMode]);

  // 💾 Auth & Auto-Resume Logic
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setAuthUser(user);
      if (user) {
        if (user.displayName) {
          setDisplayName(user.displayName);
          localStorage.setItem("displayName", user.displayName);
        }
      }
      setAuthChecked(true);
    });
    return () => unsubscribe();
  }, []);

  // 💾 Re-hydrate Daily Events if resuming
  useEffect(() => {
    if (hasStarted && activePanel === "game" && !isPremiumMode && initialEvents.length === 0 && loadedDailyEvents.length > 0 && currentDay) {
      const events = getDailyEventsForDay(loadedDailyEvents, getDailyContentDay(currentDay));
      if (events.length > 0) {
        setInitialEvents(events);
      }
    }
  }, [hasStarted, activePanel, isPremiumMode, initialEvents, loadedDailyEvents, currentDay]);

  useEffect(() => {
    if (!currentDay || isPremiumMode) return;

    if (!hasStarted) {
      if (activePanel === "game") {
        setActivePanel("title");
      }
      return;
    }

    if (hasCurrentDailySession(currentDay)) return;

    setHasStarted(false);
    if (activePanel === "game") {
      setActivePanel("title");
    }
    setInitialEvents([]);
    setScoreFinalized(false);
    setCurrentScore(0);
    setMenuClicked(false);
    clearDailySessionStarted();
  }, [activePanel, currentDay, hasStarted, isPremiumMode]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  // 🛡️ Rendering Guard: Don't show game UI until events are loaded if we are in a game session
  const isGameReady = !hasStarted || (hasStarted && (initialEvents.length > 0 || isPremiumMode));
  const shouldShowDailyStatus =
    !isPremiumMode &&
    (activePanel === "title" || activePanel === "game") &&
    dailyAvailability !== "ready";
  const isReadyToRender = appReady && isGameReady && !shouldShowDailyStatus;

  const returnToTitle = () => {
    setHasStarted(false);
    setActivePanel("title");
    setInitialEvents([]);
    setScoreFinalized(false);
    setCurrentScore(0);
    setMenuClicked(false);
    clearDailySessionStarted();
  };

  const handlePlayUnlimited = async () => {
    let premiumPool;
    try {
      premiumPool = await loadPremiumEvents();
    } catch (error) {
      console.warn("Unlimited events failed to load:", error);
      return;
    }
    if (premiumPool.length < 5) return;
    const newEvents = getUniqueYearEvents(premiumPool, 5);
    setInitialEvents(newEvents);
    setIsPremiumMode(true);
    setHasStarted(true);
    setScoreFinalized(false);
    setCurrentScore(0);
    setActivePanel("game");
    setShowLocker(false);
    setShowOverlay(false);
    setMenuClicked(false);
    clearDailySessionStarted();
  };

  return (


    <MotionConfig reducedMotion="user">
    <div style={{ position: "relative" }}>
      <BackgroundMusic isMuted={isMuted} isActive={hasStarted && activePanel === "game"} />
      {shouldShowDailyStatus && (
        <DailyStatusScreen
          status={dailyAvailability}
          currentDay={currentDay}
          onRetry={retryDailyResources}
          onReturn={hasStarted || activePanel === "game" ? returnToTitle : null}
        />
      )}
      {!shouldShowDailyStatus && !showOverlay && !showLocker && !menuClicked && !scoreFinalized && (activePanel === "title" || activePanel === "game") && (
        <button
          aria-label="How to play"
          aria-expanded={showHowTo}
          aria-controls="how-to-play-title"
          onClick={() => {
            setShowHowTo(prev => !prev); // ✅ toggles HowTo on/off
          }}
          style={{
            position: "fixed",
            top: "1.4rem",
            left: "1.4rem",
            width: "2rem",
            height: "2rem",
            borderRadius: "50%",
            border: "none",
            background: "#e7c17f",
            color: "#034c39",
            fontSize: "1.2rem",
            fontWeight: "bold",
            cursor: "pointer",
            zIndex: 1005,

            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 0
          }}
        >
          ?
        </button>
      )}
      {/* TOP RIGHT PROFILE DISPLAY */}
      {displayName && !showHowTo && !showOverlay && !showLocker && !menuClicked && (
        <div
          style={{
            position: "fixed",
            top: "1.25rem",
            right: "1.25rem",
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            gap: "0.25rem",
            zIndex: 9999,
          }}
        >
          <div
            onClick={() => {
              setShowOverlay(true);
              setShowHowTo(false);
              setMenuClicked(true);
            }}
            style={{
              // pill styling 👇
              display: "inline-flex",
              alignItems: "center",
              gap: 8,
              padding: "6px 10px",

              borderRadius: 6,
              // background: "#034c39",
              background: "transparent",
              color: "#e7c17f",
              fontFamily: "'Baskerville', 'Garamond', 'Georgia', 'serif'",
              fontSize: "1.2rem",
              whiteSpace: "nowrap", // keep it on one line
              // cursor: "pointer",
              cursor: "default",
              pointerEvents: "none", // make it unclickable
              transition: "transform 0.15s ease, filter 0.15s ease",
              WebkitTapHighlightColor: "transparent",
              boxShadow: "none",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "scale(1.06)";
              e.currentTarget.style.filter = "brightness(1.1)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "scale(1)";
              e.currentTarget.style.filter = "none";
            }}
          >
            {/* score */}
            {/* <span style={{ fontWeight: 700, marginRight: "4rem", fontSize: "1.25rem" }}>{currentScore}</span> */}


            {/* name + optional title */}
            {/* <strong style={{ fontWeight: 700 }}>
              {displayName}
              {equippedTitle && equippedTitle !== "No Title" ? ` ${equippedTitle}` : ""}
            </strong> */}

            {/* emblem */}
            {/* <img
              src={`/emblems/${equippedEmblem}`}
              alt="Equipped Emblem"
              style={{
                width: 24,
                height: 24,
                objectFit: "contain",
                borderRadius: 4,
                marginLeft: 0, // was 6
                display: "block",
              }}
            /> */}
          </div>
        </div>
      )}

      {/* MAIN PANEL LOGIC */}
      {isReadyToRender && activePanel === "title" && !hasStarted && (
        <div>
          <TitleScreen
            onStart={() => {
              const isDailyReady = dailyAvailability === "ready";
              const isPremiumReady = loadedPremiumEvents.length > 0;

              if ((USE_RANDOM_POOL && !isPremiumReady) || (!USE_RANDOM_POOL && !isDailyReady)) {
                alert("Events are still loading. Please wait a moment.");
                return;
              }

              const events = USE_RANDOM_POOL
                ? getUniqueYearEvents(loadedPremiumEvents, 6)
                : dailyEventsForCurrentDay;

              setInitialEvents(events);
              setIsPremiumMode(false);
              setHasStarted(true);
              setScoreFinalized(false);
              setCurrentScore(0);
              setActivePanel("game");
              markDailySessionStarted(currentDay);
            }}
            setDisplayName={setDisplayName}
            currentDay={currentDay}
            setActivePanel={setActivePanel}
            setPreviousPanel={setPreviousPanel}
            autoShowAlreadyPlayed={autoShowAlreadyPlayed}
            setAutoShowAlreadyPlayed={setAutoShowAlreadyPlayed}
            authChecked={authChecked}  // 👈 Pass the state here
            authenticatedUser={authUser}
          />
        </div>
      )}

      {hasStarted && isReadyToRender && (
        <div className="fade-in" style={{ display: activePanel === "game" ? "block" : "none" }}>
          <Chronologicle
            hasStarted={hasStarted}
            displayName={displayName}
            onScoreUpdate={setCurrentScore}
            equippedEmblem={equippedEmblem}
            equippedTitle={equippedTitle}
            currentDay={currentDay}
            setCurrentDay={setCurrentDay}
            isPremiumMode={isPremiumMode}
            initialEvents={initialEvents} // ✅ add this
            scoreFinalized={scoreFinalized}           // ✅ new
            setScoreFinalized={setScoreFinalized}     // ✅ new
            setActivePanel={setActivePanel}
            setShowLocker={setShowLocker}
            onPlayUnlimited={handlePlayUnlimited} // ✅ Pass handler
          />
        </div>
      )}

      {activePanel === "leaderboard" && (

        <div style={{ backgroundColor: "#034c39", minHeight: "100vh", padding: "0" }}>
          <Leaderboard
            currentUid={displayName ? `user_${displayName}` : null}
            setMenuClicked={setMenuClicked} // ✅ pass this down
            onReturn={() => {
              setMenuClicked(false);        // ✅ restore UI when returning
              // Return to previous panel if set, otherwise default logic
              if (previousPanel) {
                setActivePanel(previousPanel);
                setPreviousPanel(null);
              } else if (hasStarted) {
                setActivePanel("game");
              } else {
                setActivePanel("title");
              }
            }}
          />
        </div>

      )}



      {activePanel === "stats" && (
        <div style={{ backgroundColor: "#034c39", height: "100dvh", overflow: "hidden" }}>
          <MyStatsPage
            onReturn={() => {
              setMenuClicked(false);
              // Return to previous panel if set, otherwise default logic
              if (previousPanel) {
                setActivePanel(previousPanel);
                setPreviousPanel(null);
              } else if (hasStarted) {
                setActivePanel("game");
              } else {
                setActivePanel("title");
              }
            }}
          />
        </div>
      )}

      {/* LOCKER MODAL */}
      {showLocker && (

        <ProfileMenu
          displayName={displayName}
          setDisplayName={setDisplayName}
          equippedEmblem={equippedEmblem}
          setEquippedEmblem={setEquippedEmblem}
          equippedTitle={equippedTitle}
          setEquippedTitle={setEquippedTitle}
          onClose={() => setShowLocker(false)}
        />

      )}
      {showHowTo && (

        <HowToPlayModal onClose={() => setShowHowTo(false)} />

      )}
      {/* OVERLAY MENU */}
      {showOverlay && (

        <MenuOverlay
          onClose={() => {
            setShowOverlay(false);
            setMenuClicked(false); // ✅ good
          }}

          onLeaderboard={() => {
            setActivePanel("leaderboard");
            setMenuClicked(true); // ✅ make sure UI stays hidden
            setTimeout(() => {
              setShowOverlay(false);
            }, 0);
          }}
          onStats={() => {
            setActivePanel("stats");
            setMenuClicked(true);
            setTimeout(() => {
              setShowOverlay(false);
            }, 0);
          }}
        />

      )}

    </div>
    </MotionConfig>

  );
}

export default App;
